import { format } from 'date-fns';
import { ShapingService } from '../../../../services';
import { LinePriceDetails } from '../../../../components/lines-editor/base-doc-line-editor/base-doc-line-editor';
import { CommonModule } from '@angular/common';
import { Component, Injector, Input, output, ViewChild } from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import { ColDef, ColSpanParams, ICellEditorParams } from '@ag-grid-community/core';
import {
  BaseDocDto,
  CustomerProjectDto,
  CustomerProjectService,
  DocLineRole,
  DocLineUpdatedDto,
} from '@digiwall/goliath-documents/proxy';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';

import { ObserverQueue } from '@digiwall/core';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { BaseCustomerDocLineEditor } from '../../../base-customer-docs/lines/base-customer-doc-lines-editor.component';
import { GroupRowCellRendererComponent } from '@digiwall/goliath-core';
import {
  FieldTypes,
  ITypeAheadCellEditorParams,
  TypeAheadCellEditorComponent,
} from '@digiwall/ag-grid';
import { ArticleSelectionLookupDto } from '@digiwall/goliath-products/proxy';
import { DocRole, DocTextDto } from '@digiwall/goliath-core/proxy';

@Component({
  selector: 'document-customer-project-lines-grid',
  templateUrl: './customer-project-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export default class CustomerProjectLinesEditorComponent extends BaseCustomerDocLineEditor<CustomerProjectDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;
  @Input() set customerProject(value) {
    this._document = value;
    this.setLanguageCode();
  }

  @Input() set readonly(value) {
    this._readonly = value;
  }
  public override lineUpdatedEvent = output<Partial<DocLineUpdatedDto>>();
  public linePricingDetailsEvent = output<LinePriceDetails>();

  protected override handlingLinesUpdatedEvent = true;

  protected override articleLineRole: DocLineRole = DocLineRole.CustomerProjectArticleLine;
  protected override textLineRole: DocLineRole = DocLineRole.CustomerProjectTextLine;
  protected override chapterLineRole: DocLineRole = DocLineRole.CustomerProjectChapterLine;

  public columnDefs: Array<ColDef> = [];

  protected computationModuleActive: boolean = false;

  constructor(
    protected localizationService: LocalizationService,
    protected docService: CustomerProjectService,
    protected shapingService: ShapingService,
    protected injector: Injector,
    protected updateQueue: ObserverQueue,
    protected confirmationService: ConfirmationService,
  ) {
    super(
      localizationService,
      docService,
      shapingService,
      injector,
      updateQueue,
      confirmationService,
    );

    this.initColDefs();
  }

  protected colDefs(): ColDef<any, any>[] {
    let colDefs: ColDef<any, any>[] = [
      {
        field: 'lineFromIdRow',
        headerName: '▼',
        showRowGroup: 'lineFromId',
        cellRenderer: GroupRowCellRendererComponent,
        suppressHeaderMenuButton: true,
        pinned: 'left',
        suppressMovable: true,
        width: 75,
        lockVisible: true,
      },
      this.colDef_displayReference,
      {
        colId: BaseThirdPartyDocLineEditor.CODE_COL_ID,
        field: 'code',
        pinned: 'left',
        width: 180,
        type: FieldTypes.String,
        headerName: this.localizationService.instant('Doc::CustomerReference'),
        editable: params =>
          params.data.lineRole == this.articleLineRole && this.isCellEditable(params),
        cellEditorSelector: (params: ICellEditorParams<any, any>) => {
          if (params.data?.lineRole == this.articleLineRole) {
            return {
              component: TypeAheadCellEditorComponent<BaseDocDto, ArticleSelectionLookupDto>,
              params: {
                lookupFn: term => this.articleLookupFn(term),
                formatFn: item => (typeof item === 'string' ? item : item.label),
                displayLabelFn: item => item.code,
                callbackFn: this.onArticleSelected.bind(this),
                callBackTrimSearch: this.codeCallBackTrimSearch.bind(this),
              } as ITypeAheadCellEditorParams<BaseDocDto, ArticleSelectionLookupDto>,
            };
          } else {
            return { component: 'agTextCellEditor' };
          }
        },
      },
      {
        field: 'description',
        width: 400,
        headerName: this.localizationService.instant('::Description'),
        editable: params => this.isCellEditable(params),
        lockPinned: true,
        cellEditorParams: {
          rows: 15,
          cols: 150,
          maxLength: 100000,
        },
        cellEditorSelector: ({ data }: { data }) => {
          if (data?.lineRole === this.textLineRole) {
            return {
              component: TypeAheadCellEditorComponent<BaseDocDto, DocTextDto>,
              params: {
                lookupFn: searchTerm =>
                  this.docTextService.search(searchTerm, DocRole.CustomerQuote),
                formatFn: item =>
                  typeof item === 'string'
                    ? item
                    : item.code + ' - ' + item.name.allTranslations[this.languageCode],
                callbackFn: ({ line, selectedItem }: { line: any; selectedItem: DocTextDto }) => {
                  this._updateQueue
                    .enqueue(() => {
                      return this._docService.setLineDescription(
                        this._document.id,
                        this._document.concurrencyStamp,
                        line.id,
                        (selectedItem as DocTextDto).text.allTranslations[this.languageCode],
                      );
                    })
                    .subscribe(response => this._updateLines(response));
                },
              } as ITypeAheadCellEditorParams<BaseDocDto, DocTextDto>,
            };
          }
          return {
            component:
              data?.lineRole == this.chapterLineRole ? 'agTextCellEditor' : 'agLargeTextCellEditor',
          };
        },
        colSpan: (params: ColSpanParams) => {
          if (params.context?.isExcelExport) {
            return 1;
          }

          switch (params.data?.lineRole) {
            case this.articleLineRole:
              return 1;
            case this.textLineRole:
            case this.chapterLineRole:
              return 28;
            default:
              return 1;
          }
        },
        autoHeight: true,
        cellStyle: { 'white-space': 'pre-line' },
      },
      this.colDef_Quantity,
      {
        field: 'customerDocDisplayNumber',
        width: 100,
        headerName: this.localizationService.instant(
          'Doc::CustomerProject:CustomerDocDisplayNumber',
        ),
        editable: false,
        type: FieldTypes.String,
        suppressMovable: true,
      },
      {
        field: 'customerDocDate',
        width: 100,
        headerName: this.localizationService.instant('Doc::CustomerProject:CustomerDocDate'),
        editable: false,
        type: FieldTypes.Date,
        suppressMovable: true,
      },
    ];

    return colDefs;
  }

  protected initColDefs(): void {
    this.columnDefs = this.colDefs();
  }
}
